import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Data Quality-vy för bevakade flyg.
 *
 * Visar field coverage (% av watches som har varje kanoniskt fält ifyllt),
 * problem patterns (försvunna takeoff-tider, statusar som UNKNOWN m.fl.)
 * och canonical-projection-fel.
 *
 * Endast admin/vuxen (backend gärdar).
 */
export function DataQualityCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/flights/data-quality");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunde inte hämta data-kvalitet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  if (loading && !data) {
    return (
      <div className="card-soft p-4 flex items-center gap-2 text-sm text-[color:var(--text-tertiary)]">
        <Loader2 size={14} className="animate-spin" /> Laddar data-kvalitet…
      </div>
    );
  }
  if (error) {
    return (
      <div className="card-soft p-4 text-sm text-red-600">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const { total, canonical_check, fields, problems, by_kind, by_status } = data;
  const canonicalPct = total ? Math.round(100 * canonical_check.ok / total) : 0;
  const totalProblems = Object.values(problems || {}).reduce((a, b) => a + b, 0);

  const sortedFields = Object.entries(fields).sort((a, b) => b[1].pct - a[1].pct);

  return (
    <section data-testid="data-quality-card" className="card-soft p-4 space-y-4">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[color:var(--accent-color,#6366f1)]" />
          <h3 className="text-sm font-semibold">Data Quality</h3>
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[color:var(--bg-elevated)] text-[color:var(--text-tertiary)]">
            {total} watches
          </span>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 rounded text-[11px] hover:bg-[color:var(--bg-elevated)] disabled:opacity-50"
          data-testid="data-quality-refresh"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          Uppdatera
        </button>
      </header>

      {/* Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile label="Kanonisk OK" value={`${canonical_check.ok}/${total}`} pct={canonicalPct} accent />
        <Tile label="Problem" value={totalProblems} warn={totalProblems > 0} />
        <Tile label="Avgångar" value={by_kind?.departure || 0} />
        <Tile label="Ankomster" value={by_kind?.arrival || 0} />
      </div>

      {/* Field coverage */}
      <div>
        <h4 className="text-xs font-medium uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
          Field Coverage
        </h4>
        <div className="space-y-1">
          {sortedFields.map(([key, info]) => (
            <CoverageBar key={key} field={key} pct={info.pct} count={info.count} total={info.total} />
          ))}
        </div>
      </div>

      {/* Problems */}
      {totalProblems > 0 && (
        <div>
          <h4 className="text-xs font-medium uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2 flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-amber-600" />
            Problem
          </h4>
          <div className="space-y-1">
            {Object.entries(problems).filter(([_, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className="text-[color:var(--text-secondary)]">{problemLabel(k)}</span>
                <span className="font-semibold text-amber-600 tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status distribution */}
      <div>
        <h4 className="text-xs font-medium uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
          Statusfördelning
        </h4>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(by_status || {}).sort((a, b) => b[1] - a[1]).map(([code, n]) => (
            <span
              key={code}
              className="text-[11px] px-2 py-0.5 rounded-full bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] tabular-nums"
              data-testid={`status-${code}`}
            >
              {code}: {n}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Tile({ label, value, pct, accent, warn }) {
  return (
    <div className="rounded-md bg-[color:var(--bg-elevated)] p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${warn ? "text-amber-600" : accent ? "text-[color:var(--accent-color,#6366f1)]" : ""}`}>
        {value}
        {pct !== undefined && <span className="text-xs font-normal opacity-70 ml-1">{pct}%</span>}
      </div>
    </div>
  );
}

function CoverageBar({ field, pct, count, total }) {
  const tone = pct >= 90 ? "bg-emerald-500"
    : pct >= 70 ? "bg-emerald-400"
    : pct >= 50 ? "bg-amber-500"
    : "bg-red-500";
  return (
    <div className="space-y-0.5">
      <div className="flex items-baseline justify-between gap-2 text-[11px]">
        <span className="text-[color:var(--text-secondary)] font-mono truncate">{field}</span>
        <span className="tabular-nums text-[color:var(--text-tertiary)]">
          {count}/{total} · {pct}%
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-[color:var(--bg-elevated)] overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function problemLabel(k) {
  return ({
    unknown_status: "Watches utan kanonisk status",
    missing_distance: "Saknar avståndsberäkning",
    no_aircraft_reg: "Saknar aircraft registration",
    departures_missing_takeoff: "Landade avgångar utan takeoff-tid",
    arrivals_missing_landing: "Landade ankomster utan landningstid",
    canonical_projection_errors: "Kanonisk projektion fel",
  })[k] || k;
}
